<template>
  <div class="content-frame result-display">
    <h1 class="u-reset content-frame__title result-display__title">
      {{ title }}
      <template v-if="savedName">
        <br />
        {{ name }}
      </template>
      {{ relativeResult }}%
      <br />
      <span class="result-display__sub-title">
        ({{ absoluteResult }} {{ t('common.result.of') }} {{ maxResult }} {{ currency }})
      </span>
    </h1>

    <div class="result-display__evaluation">
      <p
        class="u-reset result-display__evaluation-text"
        v-html="t(`${topic}.result.evaluation_${evaluation}_text`)"
      />
    </div>

    <div class="result-display__text">
      <p class="u-reset content-frame__lead">
        {{ t(`${topic}.result.text`) }}
      </p>
      <p v-html="t(`${topic}.result.cta`)" />
    </div>

    <ResetBtn :btn-facets="['primary', 'medium']" />
  </div>
</template>

<script lang="ts">
  import { computed, defineComponent } from 'vue'
  import { useQuiz } from '@/api/quiz/quiz'
  import ResetBtn from '@/components/ResetBtn/ResetBtn.vue'
  import { useI18n } from 'vue-i18n'
  import useCurrentTopic from '@/api/current-topic/current-topic'
  import { useLocalStorage } from '@/api/local-storage/local-storage'
  import { LocalStorageNameKey } from '@/model/quiz-file'

  export default defineComponent({
    components: { ResetBtn },
    setup() {
      const { t } = useI18n({ useScope: 'global' })
      const { getItem } = useLocalStorage()
      const { topic } = useCurrentTopic()
      const { quiz, answers, questions } = useQuiz()

      const maxResult = computed<number>(() => {
        if (!quiz.value) {
          return 0
        }

        if (quiz.value?.usesPoints) {
          return questions.value.reduce((qAcc, qValue) => {
            const max = qValue.answers.reduce((aAcc, aValue) => {
              return Math.max(aAcc, aValue.points || 0)
            }, 0)
            return qAcc + max
          }, 0)
        } else {
          return questions.value.length
        }
      })

      const absoluteResult = computed<number>(() => {
        if (!quiz.value) {
          return 0
        }

        return questions.value.reduce((acc, value, idx) => {
          const answer = value.answers[answers.value[idx]]

          if (quiz.value?.usesPoints) {
            return acc + (answer.points || 0)
          } else {
            return acc + (answer.correct ? 1 : 0)
          }
        }, 0)
      })

      const relativeResult = computed<number>(() => {
        return Math.round((absoluteResult.value / maxResult.value) * 100)
      })

      const evaluation = computed<number>(() => {
        switch (true) {
          case absoluteResult.value > (3 / 4) * maxResult.value:
            return 4
          case absoluteResult.value > (2 / 4) * maxResult.value:
            return 3
          case absoluteResult.value > (1 / 4) * maxResult.value:
            return 2
          default:
            return 1
        }
      })

      const savedName = computed<string>(() => {
        return getItem<string>(`${topic.value}.${LocalStorageNameKey}`)
      })

      const name = computed<string>(() => {
        return `${savedName.value ? ` ${t(`common.result.of`)} ${savedName.value}:` : ''}`
      })

      const currency = computed<string>(() => {
        return t(`common.result.${quiz.value?.usesPoints ? 'points' : 'right'}`)
      })

      const title = computed<string>(() => {
        const titleText = t(`common.result.title`)
        return `${titleText}${savedName.value ? '' : ':'}`
      })

      return {
        absoluteResult,
        relativeResult,
        name,
        currency,
        savedName,
        t,
        title,
        topic,

        maxResult,
        evaluation,
      }
    },
  })
</script>

<style lang="scss" src="./_ResultDisplay.scss" />
